import { Controller } from "@hotwired/stimulus"

// Instant, client-side search + browse-by-letter for the menopause glossary.
// The full term list is rendered server-side; this controller just toggles
// visibility and handles smooth letter navigation.
export default class extends Controller {
  static targets = ["input", "clear", "term", "section", "letterLink", "empty", "chrome"]

  connect() {
    this.filter()
    this.scrollHandler = () => this.syncActiveLetter()
    window.addEventListener("scroll", this.scrollHandler, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler)
  }

  filter() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim().toLowerCase() : ""
    let anyVisible = false

    this.termTargets.forEach((term) => {
      const haystack = term.dataset.search || ""
      const match = query === "" || haystack.includes(query)
      term.hidden = !match
      if (match) anyVisible = true
    })

    // Hide letter sections (and their headings) that have no visible terms.
    this.sectionTargets.forEach((section) => {
      const hasVisible = section.querySelector("[data-glossary-target='term']:not([hidden])")
      section.hidden = !hasVisible
    })

    if (this.hasClearTarget) this.clearTarget.hidden = query === ""
    if (this.hasEmptyTarget) this.emptyTarget.hidden = anyVisible
  }

  clear() {
    if (!this.hasInputTarget) return
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.filter()
  }

  jump(event) {
    event.preventDefault()
    const letter = event.currentTarget.dataset.letter
    const section = this.sectionTargets.find((el) => el.dataset.letter === letter)
    if (!section) return

    section.scrollIntoView({ behavior: "smooth", block: "start" })
    this.setActiveLetter(letter)
  }

  setActiveLetter(letter) {
    this.letterLinkTargets.forEach((link) => {
      link.classList.toggle("is-active", link.dataset.letter === letter)
    })
  }

  // Highlights the letter for the section currently nearest the top of the view.
  syncActiveLetter() {
    const marker = this.activeLetterMarker()
    let current = null

    this.sectionTargets.forEach((section) => {
      if (section.hidden) return
      if (section.getBoundingClientRect().top <= marker) {
        current = section.dataset.letter
      }
    })

    if (current) this.setActiveLetter(current)
  }

  // A section counts as "current" once its top passes just below the sticky
  // search + letters chrome. Measured live so it stays correct if the chrome's
  // height changes. The slack must exceed the gap between the chrome and the
  // letter-jump landing position (scroll-margin-top), or a jumped-to section
  // sits a fraction of a pixel past the marker and never reads as current.
  activeLetterMarker() {
    if (!this.hasChromeTarget) return 140

    return this.chromeTarget.getBoundingClientRect().bottom + 24
  }
}
